<template>
  <main-template>
    <v-container>
      <v-row>
        <v-col cols="12">
          <v-btn color="primary" x-small text @click="$router.go(-1)">
            <v-icon left>fa fa-angle-left</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <v-row justify="center">
        <v-col cols="12" sm="8" md="6">
          <div v-if="carregando" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" />
          </div>

          <template v-else>
            <!-- Sem parcelas em aberto -->
            <div class="d-flex justify-space-between align-center mb-2">
              <span class="text-subtitle-2 grey--text text--darken-1">Mensalidade do Mês</span>
              <router-link
                :to="{
                  name: 'matriculas.mensalidades.lista',
                  params: { matricula_id: matriculaId },
                }"
                class="text-caption primary--text"
              >
                Ver todas &rsaquo;
              </router-link>
            </div>
            <v-card
              v-if="!proximaParcela && taxasDoMes.length === 0"
              class="pa-6 text-center"
              rounded="lg"
            >
              <v-icon size="64" color="success">mdi-check-circle-outline</v-icon>
              <div class="text-h6 mt-4">Tudo em dia!</div>
              <div class="text-body-2 grey--text mt-2">
                Não há mensalidades em aberto para esta matrícula.
              </div>
            </v-card>

            <!-- Card Mensalidade do Mês -->
            <v-card v-else-if="proximaParcela" rounded="lg">
              <v-card-text>
                <div class="text-caption grey--text mb-1">Total a pagar na próxima mensalidade</div>
                <div
                  v-if="proximaParcela.temDesconto"
                  class="text-body-1 grey--text text-decoration-line-through"
                >
                  {{ proximaParcela.valor | currency }}
                </div>
                <div class="text-h4 font-weight-bold primary--text mb-2">
                  {{ proximaParcela.valorEmAberto | currency }}
                </div>

                <div v-if="proximaParcela.referencia_3" class="text-caption grey--text mb-1">
                  Vencimento em
                  {{
                    proximaParcela.referencia_3 | dateParse("YYYY-MM-DD") | dateFormat("DD/MM/YYYY")
                  }}
                </div>

                <div v-if="proximaParcela.descontosTiers.length > 0" class="mb-4">
                  <div
                    v-for="(tier, index) in proximaParcela.descontosTiers"
                    :key="index"
                    class="text-caption"
                    :class="
                      tierExpirado(tier)
                        ? 'grey--text text--lighten-1'
                        : tier.valor == proximaParcela.descontoAplicavel
                        ? 'green--text text--darken-1 font-weight-bold'
                        : 'grey--text text--lighten-1'
                    "
                    :style="tierExpirado(tier) ? 'text-decoration: line-through' : ''"
                  >
                    Desconto de {{ tier.valor | currency }} até
                    {{ tier.data | dateParse("YYYY-MM-DD") | dateFormat("DD/MM/YYYY") }}
                  </div>
                </div>
                <div v-else class="mb-4"></div>

                <v-btn color="primary" block @click="selecionarParcelaInicial(proximaParcela)">
                  Pagar mensalidade
                </v-btn>

                <div class="text-center mt-3">
                  <router-link
                    :to="{
                      name: 'matriculas.mensalidades.detalhe',
                      params: {
                        parcela_id: proximaParcela.id,
                        matricula_id: matriculaId,
                      },
                    }"
                    class="text-caption primary--text"
                  >
                    Ver detalhes
                  </router-link>
                </div>
              </v-card-text>
            </v-card>

            <!-- Cards de taxa do mês -->
            <template v-if="taxasDoMes.length > 0">
              <div class="text-subtitle-2 grey--text text--darken-1 mb-2 mt-6">
                {{ taxasDoMes.length > 1 ? "Taxas do Mês" : "Taxa do Mês" }}
              </div>

              <v-card v-for="taxa in taxasDoMes" :key="taxa.id" rounded="lg" class="mb-3">
                <v-card-text>
                  <div class="text-caption grey--text mb-1">
                    {{ taxa.descricaoMensalidade }}
                  </div>
                  <div class="text-h4 font-weight-bold primary--text mb-2">
                    {{ taxa.valorEmAberto | currency }}
                  </div>

                  <div v-if="taxa.referencia_3" class="text-caption grey--text mb-1">
                    Vencimento em
                    {{ taxa.referencia_3 | dateParse("YYYY-MM-DD") | dateFormat("DD/MM/YYYY") }}
                  </div>

                  <div v-if="taxa.estaAtrasada" class="text-caption red--text text--darken-1 mb-4">
                    Em atraso — já inclui multa de {{ taxa.multaCalculada | currency }} e juros de
                    {{ taxa.jurosCalculados | currency }}
                  </div>
                  <div v-else class="mb-4"></div>

                  <v-btn color="primary" block @click="selecionarParcelaInicial(taxa)">
                    Pagar taxa
                  </v-btn>

                  <div class="text-center mt-3">
                    <router-link
                      :to="{
                        name: 'matriculas.mensalidades.detalhe',
                        params: {
                          parcela_id: taxa.id,
                          matricula_id: matriculaId,
                        },
                      }"
                      class="text-caption primary--text"
                    >
                      Ver detalhes
                    </router-link>
                  </div>
                </v-card-text>
              </v-card>
            </template>
          </template>
        </v-col>
      </v-row>
    </v-container>
  </main-template>
</template>

<script>
import { mapGetters } from "vuex";
import Parcela from "@/Models/Parcela";

export default {
  name: "MensalidadeAluno",

  computed: {
    ...mapGetters("Mensalidades", ["matriculaSelecionada"]),

    matriculaId() {
      return this.$route.params.matricula_id;
    },

    parcelas() {
      if (!this.matriculaSelecionada?.parcelas) return [];
      const aluno = this.matriculaSelecionada.aluno || {};
      const responsavel = aluno.responsavel || null;

      return this.matriculaSelecionada.parcelas.map(
        (p) => new Parcela({ ...p, aluno: { ...aluno, responsavel } })
      );
    },
    parcelasEmAberto() {
      return this.parcelas.filter((p) => p.estaEmAberto).sort((a, b) => a.numero - b.numero);
    },

    proximaParcela() {
      return this.parcelasEmAberto.find((p) => !p.ehTaxa && this.ehDoMesAtual(p)) || null;
    },

    taxasDoMes() {
      return this.parcelasEmAberto.filter((p) => p.ehTaxa && this.ehDoMesAtual(p));
    },

    totalParcelasProximoMes() {
      if (!this.proximaParcela) return 0;
      return this.parcelasEmAberto.filter(
        (p) => p.mesAnoReferencia === this.proximaParcela.mesAnoReferencia
      ).length;
    },

    totalDesconto() {
      if (!this.proximaParcela) return 0;
      return this.parcelasEmAberto
        .filter((p) => p.mesAnoReferencia === this.proximaParcela.mesAnoReferencia)
        .reduce((acc, p) => acc + p.desconto, 0);
    },
  },

  data() {
    return {
      carregando: true,
    };
  },

  async mounted() {
    await this.carregarDados();
  },

  methods: {
    async carregarDados() {
      this.carregando = true;
      try {
        const matricula = await this.$services.matriculasService.pegarMatricula(this.matriculaId);
        const matriculaDetalhada = await this.$services.matriculasService.getDetails(matricula.id);
        this.$store.commit("Mensalidades/setMatriculaSelecionada", matriculaDetalhada);
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregando = false;
      }
    },

    tierExpirado(tier) {
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      return new Date(`${tier.data}T23:59:59`) < hoje;
    },

    ehDoMesAtual(parcela) {
      if (!parcela.referencia_3) return false;
      const hoje = new Date();
      const partes = parcela.referencia_3.split("-");
      return (
        parseInt(partes[0], 10) === hoje.getFullYear() &&
        parseInt(partes[1], 10) === hoje.getMonth() + 1
      );
    },

    selecionarParcelaInicial(parcela) {
      this.$router.push({
        name: "matriculas.mensalidades.formaPagamento",
        params: { matricula_id: this.matriculaId, parcelaId: parcela.id },
      });
    },
  },
};
</script>
